import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Badge from "../components/Badge";
import Banner from "../components/Banner";
import Card from "../components/Card";
import ChevronLeft from "../components/icons/ChevronLeft";
import LinkButton from "../components/LinkButton";
import { RowSpinner } from "../components/Loader/Loader";
import { accountTitle, formatDate, providerName } from "../components/syncunit/format";
import OperationsTimeline from "../components/syncunit/OperationsTimeline";
import ReportTimeline from "../components/syncunit/ReportTimeline";
import Tabs from "../components/syncunit/Tabs";
import { MY_ACCOUNTS_PATH, syncUnitPath } from "../constants/syncRoutes";
import { useRouteParams } from "../hooks/use-navigation";
import useToken from "../hooks/use-token";
import {
  useListInvestingAccountBrokerReportsQuery,
  useListInvestingAccountOperationsQuery,
  useReadInvestingAccountQuery,
} from "../store";

// The first operations page OperationsTimeline reads — the same args, so RTK Query shares one request.
const FIRST_PAGE = { limit: 50 };
const STALE = new Set([404, 409]);

function CenteredCard({ children }) {
  return (
    <Card data-testid="centered-card" className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-sm text-text-muted">
      {children}
    </Card>
  );
}

// SCR-05 — the confirmed chronology of an account, read only, addressed by the account id (ADR-0002). A read of the
// shown version answering 404 / 409 means a newer confirmation replaced it: re-read the account and rebuild, silently.
function InvestingAccountPage() {
  const { t } = useTranslation();
  const token = useToken();
  const { accountId } = useRouteParams();
  const [tab, setTab] = useState("operations");

  const { data, error, isLoading, refetch } = useReadInvestingAccountQuery(
    { accountId },
    { skip: !token || !accountId, refetchOnMountOrArgChange: true },
  );
  const version = data?.confirmedSyncUnit || null;
  const versionArgs = { accountId, syncUnitId: version?.id };
  const { data: operations, error: operationsError } = useListInvestingAccountOperationsQuery(
    { ...versionArgs, ...FIRST_PAGE },
    { skip: !version },
  );
  const { data: reports, error: reportsError } = useListInvestingAccountBrokerReportsQuery(versionArgs, { skip: !version });

  const staleVersion = [operationsError, reportsError].some((failure) => STALE.has(failure?.status));
  useEffect(() => {
    if (staleVersion) refetch();
  }, [staleVersion, refetch]);

  const back = (
    <LinkButton to={MY_ACCOUNTS_PATH}>
      <ChevronLeft />
      {t("investingAccount.back")}
    </LinkButton>
  );

  let body;
  if (isLoading || (!data && !error)) {
    body = (
      <CenteredCard>
        <RowSpinner />
        {t("investingAccount.loading")}
      </CenteredCard>
    );
  } else if (error && error.status !== 404) {
    body = (
      <Banner tone="danger">
        <p>{t("investingAccount.error")}</p>
        <LinkButton onClick={refetch}>{t("investingAccount.retry")}</LinkButton>
      </Banner>
    );
  } else if (!version) {
    body = <CenteredCard>{t("investingAccount.notFound")}</CenteredCard>;
  } else {
    body = (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight break-all min-w-0">{accountTitle(data.investingAccount)}</h1>
          <Badge tone="accent">{providerName(data.investingAccount.dataProvider)}</Badge>
        </div>
        <p className="text-xs text-text-muted">
          {t("investingAccount.lastSync", { date: formatDate(version.confirmDateTime) })}
          {data.openSyncUnit && (
            <>
              {" · "}
              <LinkButton to={syncUnitPath(accountId)} className="text-xs">
                {t("investingAccount.openSync")}
              </LinkButton>
            </>
          )}
        </p>
        <Tabs
          label={accountTitle(data.investingAccount)}
          active={tab}
          onChange={setTab}
          tabs={[
            { id: "operations", label: t("investingAccount.tabs.operations", { count: operations?.totalCount ?? 0 }) },
            { id: "reports", label: t("investingAccount.tabs.reports", { count: reports?.reports?.length ?? 0 }) },
          ]}
        />
        {staleVersion ? null : tab === "operations" ? (
          <OperationsTimeline key={version.id} accountId={accountId} syncUnitId={version.id} showOrigin={false} onStale={refetch} />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-text-muted">{t("investingAccount.reportsHint")}</p>
            {reports && <ReportTimeline reports={reports.reports} gaps={reports.gaps} mode="confirmed" />}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-5 md:px-10 py-8 max-w-[1040px]" data-testid="investing-account-page" data-account-id={accountId}>
      {back}
      {body}
    </div>
  );
}

export default InvestingAccountPage;
